import React, { useEffect, useMemo, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import useXlsxDownload from "pages/EventsManagement/components/Tabs/Logistics/useXlsxDownload"
import { organizerListPath, seasonLabel } from "./plannerQuery"
import { categoryLabel, competitionsLabel, dayLabel, playModeLabel, plural, sportLabel, todayIn } from "./plannerLabels"
import { addDays } from "./plannerCalendar"
import { HOW, LANGUAGES, LIST_SECTIONS, changesLine, failedLine, instantDaysLabel, lastSendingLine, listAsText, queueClubLabel, queueOrganizerName,
  reasonsLabel } from "./sendingLabels"
import AddressPicker, { addressesReady, initialAddresses, recipientsOf } from "./AddressPicker"
import useOrganizerLists from "./useOrganizerLists"
import QueueBar from "./QueueBar"
import SendReviewDialog from "./SendReviewDialog"
import ResendDialog from "./ResendDialog"

// What a To send entry carries anew (Phase C2a's read): the drafts ready to send, the proposals the organizer was not sent, the changed
// ones and the withdrawals to announce.
const somethingNew = entry => entry.ready.length + entry.notSentYet.length + entry.changed.length + entry.withdrawals.length > 0
const countsLine = entry => {
  const parts = [entry.ready.length + entry.notSentYet.length && `${entry.ready.length + entry.notSentYet.length} new`,
    entry.changed.length && `${entry.changed.length} changed`, entry.withdrawals.length && `${entry.withdrawals.length} withdrawn`].filter(Boolean)
  return parts.join(" · ") || "Nothing new"
}
// What a sending carries of the organizer's list (Phase C2a): its New items without the drafts left unticked, the changed ones, those still
// waiting and the confirmed ones; the withdrawals are announced apart. `on` holds the ids of the drafts ticked.
const newCarried = (list, on) => list.new.filter(item => item.state !== "draft" || on.has(item.id))
const carriedOf = (list, on) => [...newCarried(list, on), ...list.changed, ...list.waiting, ...list.confirmed]

// To send (wayfinder ticket 16; Phase C3): the organizers with something to send — the ready drafts of the period, the proposals changed
// after they were sent, the withdrawals to announce — unticked by default, each with its whole current list to review; the drafts that
// can't be sent with their reasons; "Email failed" until a list reaches the organizer. Nothing is sent automatically: "Review and send"
// is the confirmation, and there is no Undo after it (an email can't be recalled). One sending per organizer, all or nothing.
export default function ToSendTab({ query, read, period, onPeriod, onCountry, settings, timeZone, focus, onFocusUsed, commands, onOpenProposal,
  onOpenWaiting }) {
  const entries = useMemo(() => read.data?.organizers || [], [read.data])
  const unavailable = read.data?.unavailable || []
  const [included, setIncluded] = useState({})
  const [selectedId, setSelectedId] = useState(null)
  // What the SuperAdmin chose per organizer: the drafts ticked (all by default), how the list goes, its language and its addresses.
  const [picks, setPicks] = useState({})
  const [reviewing, setReviewing] = useState(null)
  const [banner, setBanner] = useState(null)
  const [resending, setResending] = useState(null)
  const download = useXlsxDownload("The list could not be downloaded. Try again.")

  // A selection from the List or "Send to organizer…" (C3 pinned decision Q2): those organizers included, exactly those drafts ticked.
  // It is used once: the page forgets it, so coming back to the tab keeps what the SuperAdmin chose since.
  const [fromSelection, setFromSelection] = useState(null)
  useEffect(() => {
    if (!focus || !read.data) return
    onFocusUsed()
    const offered = entries.filter(entry => focus.organizerIds.includes(entry.organizerId))
    setIncluded(Object.fromEntries(offered.filter(somethingNew).map(entry => [entry.organizerId, true])))
    if (offered.length) setSelectedId(offered[0].organizerId)
    // "Send the updated list" names an organizer only: its drafts keep their default.
    if (!focus.ids.length) return setFromSelection(null)
    setPicks(current => ({ ...current, ...Object.fromEntries(offered.map(entry => [entry.organizerId,
      { ...current[entry.organizerId], drafts: Object.fromEntries(entry.ready.map(row => [row.id, focus.draftIds.includes(row.id)])) }])) }))
    const listed = new Set(entries.flatMap(entry => [...entry.ready, ...entry.notSentYet, ...entry.changed, ...entry.cannotBeSent].map(({ id }) => id))
      .concat((read.data.withoutOrganizer || []).map(({ id }) => id)))
    return setFromSelection({ organizers: offered.length, missing: focus.numbers.filter((number, index) => !listed.has(focus.ids[index])) })
  }, [focus, read.data, entries, onFocusUsed])

  const selected = entries.find(entry => entry.organizerId === selectedId) || entries[0] || null
  const pathOf = entry => organizerListPath(entry.organizerId, entry.ready.map(({ id }) => id))
  const generation = useMemo(() => ({}), [read.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const wanted = entries.filter(entry => included[entry.organizerId] || entry === selected)
  const lists = useOrganizerLists(wanted.map(pathOf), generation)

  // The choices of one organizer, with their defaults once its list is read.
  const pickOf = entry => {
    const pick = picks[entry.organizerId] || {}
    const list = lists.listOf(pathOf(entry)).data
    const emailable = Boolean(list?.team?.email)
    return {
      drafts: pick.drafts || Object.fromEntries(entry.ready.map(({ id }) => [id, true])),
      how: pick.how || (list && !emailable ? "outsidePlatform" : "email"),
      language: pick.language || list?.organizer.language || "ro",
      address: pick.address || initialAddresses(list?.organizer),
      emailable,
      list,
    }
  }
  const setPick = (entry, changes) => setPicks(current => ({ ...current, [entry.organizerId]: { ...current[entry.organizerId], ...changes } }))
  // The drafts ticked among those To send offers now (a draft gone since is no longer ticked).
  const ticked = entry => entry.ready.filter(({ id }) => pickOf(entry).drafts[id]).map(({ id }) => id)
  // The address of an included email still to check.
  const toCheck = entry => {
    const pick = pickOf(entry)
    return Boolean(included[entry.organizerId] && pick.list && pick.how === "email" && pick.emailable && !addressesReady(pick.address))
  }
  // Why an included organizer can't go into "Review and send" yet, or null: its list as To send reads it now (a sending is built from
  // it), and a proposal of the list already Proposed that fails the sending's checks (Phase C2a) fixed first, since the sending names the
  // whole list and would be refused.
  const notReady = entry => {
    const pick = pickOf(entry), list = lists.listOf(pathOf(entry))
    if (!list.data || !list.fresh || list.error) return "its list is loading"
    const stuck = entry.cannotBeSent.filter(({ state }) => state !== "draft")
    if (stuck.length) return `fix ${stuck.map(({ number }) => number).join(", ")} first`
    if (!ticked(entry).length && !entry.notSentYet.length && !entry.changed.length && !entry.withdrawals.length) return "no draft ticked"
    if (pick.how === "email" && !pick.emailable) return "emails can't be sent from this server"
    if (toCheck(entry)) return "check the address"
    return null
  }
  // The sending of one organizer, as the command takes it (Phase C2a): the whole list at the revisions reviewed — the drafts ticked, every
  // Propusă and Confirmată the organizer holds, the withdrawals to announce.
  const sendingOf = entry => {
    const pick = pickOf(entry), list = pick.list, on = new Set(ticked(entry))
    const carried = carriedOf(list, on)
    const proposals = [...carried, ...list.withdrawn].map(({ id, revision }) => ({ id, revision }))
    const recipients = recipientsOf(pick.address)
    return {
      organizerId: entry.organizerId, name: list.organizer.name || queueOrganizerName(entry.organizer, unavailable), how: pick.how, language: pick.language,
      recipients: pick.how === "email" ? recipients : [], drafts: on.size, notSentYet: list.new.filter(item => item.state !== "draft").length,
      changed: list.changed.length, withdrawn: list.withdrawn.length, listSize: carried.length,
      body: { organizerId: entry.organizerId, how: pick.how, language: pick.language, proposals,
        ...(pick.how === "email" && { recipients, addressChecked: true }) },
    }
  }

  const inSending = entries.filter(entry => included[entry.organizerId])
  const waitingOn = inSending.filter(notReady)
  const ready = inSending.filter(entry => !notReady(entry))
  const emails = ready.filter(entry => pickOf(entry).how === "email").length
  const drafts = ready.reduce((sum, entry) => sum + ticked(entry).length, 0)
  const blocked = [...entries.flatMap(entry => entry.cannotBeSent), ...(read.data?.withoutOrganizer || [])]

  const finish = results => {
    setReviewing(null)
    const sent = results.filter(({ result }) => result.status === "saved")
    const notSent = results.filter(({ result }) => result.status !== "saved")
    // An organizer whose list went starts again from the defaults: a draft that comes later is ticked.
    const forget = current => { const next = { ...current }; sent.forEach(({ row }) => { delete next[row.organizerId] }); return next }
    setIncluded(forget)
    setPicks(forget)
    if (!results.length) return
    const proposed = sent.reduce((sum, { row }) => sum + row.drafts, 0)
    setBanner({ tone: notSent.length ? "warn" : "ok", text: [
      sent.length && `Sent: ${sent.map(({ row }) => `${row.name} (${row.how === "email" ? "email" : "sent by you"})`).join(", ")}.`,
      sent.length && (proposed ? `${plural(proposed, "proposal")} ${proposed === 1 ? "is" : "are"} now Proposed; the wait for an answer starts today.`
        : "No new proposal; the updated lists are on their way."),
      notSent.length && `Not sent: ${notSent.map(({ row }) => row.name).join(", ")}. ${notSent.length === 1 ? "It stays" : "They stay"} in To send.`,
    ].filter(Boolean).join(" ") })
  }

  const copy = async entry => {
    const pick = pickOf(entry), on = new Set(ticked(entry))
    const sections = { ...pick.list, new: newCarried(pick.list, on) }
    const text = listAsText({ name: pick.list.organizer.name || queueOrganizerName(entry.organizer, unavailable), sections, language: pick.language, settings, timeZone })
    try {
      await navigator.clipboard.writeText(text)
      commands.tell({ tone: "success", text: `${pick.list.organizer.name || "The organizer"}'s list copied as text, ready to paste in WhatsApp.` })
    } catch {
      commands.tell({ tone: "danger", text: "The list could not be copied. Download the XLSX instead." })
    }
  }

  const body = () => {
    if (read.error) return <Alert color="danger">{read.error}{" "}<button type="button" className="btn btn-link p-0 align-baseline" onClick={read.reload}>Retry</button></Alert>
    if (!read.data) return <div className="ep-loading"><Spinner size="sm" /> Loading To send…</div>
    return <div className="ep-split">
      <div className="ep-stack ep-gap ep-split-side">
        <section className="ep-card ep-organizers" aria-label="Organizers">
          <header className="ep-organizers-head">
            <input type="checkbox" aria-label="Include every organizer" checked={entries.some(somethingNew) && entries.filter(somethingNew).every(entry => included[entry.organizerId])}
              disabled={!entries.some(somethingNew)}
              onChange={event => setIncluded(event.target.checked ? Object.fromEntries(entries.filter(somethingNew).map(entry => [entry.organizerId, true])) : {})} />
            <b>Organizers</b><span className="ep-spacer" /><span className="ep-meta">{`${entries.length} with something to send`}</span>
          </header>
          {entries.map(entry => {
            const name = queueOrganizerName(entry.organizer, unavailable)
            const pick = pickOf(entry)
            return <div key={entry.organizerId} className={"ep-organizer" + (entry === selected ? " is-on" : "")}>
              <input type="checkbox" aria-label={`Include ${name}`} checked={Boolean(included[entry.organizerId])} disabled={!somethingNew(entry)}
                onChange={() => setIncluded(current => ({ ...current, [entry.organizerId]: !current[entry.organizerId] }))} />
              <button type="button" className="ep-organizer-pick" aria-pressed={entry === selected} onClick={() => setSelectedId(entry.organizerId)}>
                <b className="ep-trunc">{name}</b>
                <span className="ep-meta">{countsLine(entry)}</span>
                <span className="ep-peek-marks">
                  {included[entry.organizerId] && <span className="ep-mark ep-mark-soon">{pick.how === "email" ? "Email" : "Sent by me"}</span>}
                  {toCheck(entry) && <span className="ep-mark ep-mark-warn">Check the address</span>}
                  {included[entry.organizerId] && pick.how === "email" && pick.list && addressesReady(pick.address) && <span className="ep-mark ep-mark-ok">Address checked</span>}
                  {entry.emailFailed && <span className="ep-mark ep-mark-error">Email failed</span>}
                </span>
              </button>
            </div>
          })}
          {!entries.length && <div className="ep-empty"><b>Nothing to send</b><span className="ep-meta">No new drafts in this period and no list to update.</span></div>}
        </section>
        {blocked.length > 0 && <section className="ep-card ep-stack ep-gap" aria-label="Cannot be sent">
          <span className="ep-mark ep-mark-error">{`${plural(blocked.length, "proposal")} can't be sent`}</span>
          {blocked.some(({ state }) => state !== "draft") && <span className="ep-meta">A Proposed or Confirmed one keeps its organizer's whole list
            from being sent until it is fixed.</span>}
          {blocked.map(row => <span key={row.id} className="ep-stack">
            <span><button type="button" className="ep-link" onClick={() => onOpenProposal(row)}>{row.number}</button>
              {` · ${instantDaysLabel(row, timeZone)} · ${queueClubLabel(row.club)}`}</span>
            <span className="ep-meta">{reasonsLabel(row.reasons)}</span>
          </span>)}
        </section>}
      </div>
      {selected && <OrganizerPanel entry={selected} name={queueOrganizerName(selected.organizer, unavailable)} pick={pickOf(selected)}
        list={lists.listOf(pathOf(selected))} included={Boolean(included[selected.organizerId])} settings={settings} timeZone={timeZone}
        onInclude={() => setIncluded(current => ({ ...current, [selected.organizerId]: !current[selected.organizerId] }))}
        onPick={changes => setPick(selected, changes)} onRetry={() => lists.retry(pathOf(selected))} onOpenProposal={onOpenProposal}
        onResend={() => setResending(selected.organizerId)} onCopy={() => copy(selected)} download={download}
        onDownload={() => download.download(`/exports/tournament-proposals/organizers/${selected.organizerId}/list`,
          { params: { ...(ticked(selected).length && { drafts: ticked(selected).join(",") }), language: pickOf(selected).language } }, "Propuneri-Sportya.xlsx")} />}
    </div>
  }

  const resendEntry = entries.find(entry => entry.organizerId === resending)
  return <section className="ep-stack ep-gap" aria-label="To send">
    <QueueBar query={query} onCountry={onCountry}>
      <span className="ep-label">New drafts from</span>
      <select className="form-control ep-select" aria-label="New drafts from" value={period} onChange={event => onPeriod(event.target.value)}>
        <option value="season">{seasonLabel(query.season)} ({query.season.startsWith("winter") ? "Nov – Mar" : "Apr – Oct"})</option>
        <option value="upcoming">Every upcoming date</option>
      </select>
      <span className="ep-meta">Sending makes the new proposals Proposed. Each organizer gets their whole current list: the new proposals, the changed ones, the ones
        still waiting, the confirmed ones and, once, the ones Sportya withdrew.</span>
    </QueueBar>
    {fromSelection && <p className="ep-banner ep-banner-info mb-0" role="status">
      {`From your selection: ${plural(fromSelection.organizers, "organizer")} included.`}
      {fromSelection.missing.length > 0 && ` Nothing to send for ${fromSelection.missing.join(", ")}: already in their organizer's list as it was sent.`}</p>}
    {banner && <div className={`ep-banner ep-banner-${banner.tone} ep-banner-row`} role="status">
      <span>{banner.text}</span>
      <button type="button" className="btn btn-light btn-sm" onClick={onOpenWaiting}>Open Waiting for answer</button>
    </div>}
    {body()}
    {read.data && <div className="ep-send-bar" role="region" aria-label="This sending">
      <span className="ep-stack">
        <b>{inSending.length ? `${plural(inSending.length, "organizer")} in this sending` : "No organizer chosen"}</b>
        <span className="ep-meta">{inSending.length ? `${plural(emails, "email")} · ${ready.length - emails} sent by you · ${plural(drafts, "new proposal")} `
          + `${drafts === 1 ? "becomes" : "become"} Proposed` : "Tick the organizers on the left."}</span>
      </span>
      <span className="ep-spacer" />
      {waitingOn.length > 0 && <span className="ep-mark ep-mark-warn">{`Not ready: ${waitingOn.map(entry => `${queueOrganizerName(entry.organizer, unavailable)} (${notReady(entry)})`).join(", ")}`}</span>}
      <button type="button" className="btn btn-primary" disabled={!inSending.length || waitingOn.length > 0 || Boolean(commands.pending)}
        onClick={() => { setBanner(null); setReviewing(ready.map(sendingOf)) }}>Review and send</button>
    </div>}
    {reviewing && <SendReviewDialog rows={reviewing} answerBy={dayLabel(addDays(todayIn(timeZone), 14))} commands={commands} onClose={finish} />}
    {resendEntry && <ResendDialog entry={resendEntry} list={pickOf(resendEntry).list} name={queueOrganizerName(resendEntry.organizer, unavailable)} timeZone={timeZone}
      commands={commands} onClose={() => setResending(null)} />}
  </section>
}

// The organizer chosen on the left: how the list goes, its language and addresses, and what the organizer gets (the whole current list).
function OrganizerPanel({ entry, name, pick, list, included, settings, timeZone, onInclude, onPick, onRetry, onOpenProposal, onResend, onCopy, download, onDownload }) {
  const data = list.data
  const fullName = data?.organizer.name || name
  const isEmail = pick.how === "email"
  return <section className="ep-stack ep-gap ep-split-main" aria-label={`${fullName}'s list`}>
    <div className="ep-card ep-stack ep-gap">
      <header className="ep-panel-head">
        <span className="ep-stack"><h2>{fullName}</h2><span className="ep-meta">{lastSendingLine(entry.lastSending, timeZone)}</span></span>
        <span className="ep-spacer" />
        <label className="ep-check"><input type="checkbox" checked={included} disabled={!somethingNew(entry)} onChange={onInclude} />Include in this sending</label>
      </header>
      {entry.emailFailed && <div className="ep-banner ep-banner-error ep-banner-row" role="alert">
        <span>{failedLine(entry.lastSending, timeZone)}</span>
        <button type="button" className="btn btn-light btn-sm" disabled={!data} onClick={onResend}>Send again…</button>
      </div>}
      {list.error && <Alert color="danger" className="mb-0">{list.error}{" "}<button type="button" className="btn btn-link p-0 align-baseline" onClick={onRetry}>Retry</button></Alert>}
      {!data && !list.error && <div className="ep-loading"><Spinner size="sm" /> Loading the organizer's list…</div>}
      {data && <>
        <div className="ep-filter-row">
          <span className="ep-stack"><span className="ep-label">How it goes to the organizer</span>
            <span className="ep-segment" role="group" aria-label="How it goes to the organizer">
              {Object.entries(HOW).map(([how, label]) => <button type="button" key={how} aria-pressed={pick.how === how}
                className={"ep-chip" + (pick.how === how ? " is-on" : "")} disabled={how === "email" && !pick.emailable} onClick={() => onPick({ how })}>{label}</button>)}
            </span></span>
          <span className="ep-stack"><label className="ep-label" htmlFor="ep-send-language">Language of the list</label>
            <select id="ep-send-language" className="form-control ep-select" value={pick.language} onChange={event => onPick({ language: event.target.value })}>
              {LANGUAGES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select></span>
          <span className="ep-spacer" />
          <button type="button" className="btn btn-light btn-sm" disabled={download.busy} onClick={onDownload}>
            <i className="bx bx-download" aria-hidden="true" /> Download the list (XLSX)</button>
          <button type="button" className="btn btn-light btn-sm" onClick={onCopy}><i className="bx bx-copy" aria-hidden="true" /> Copy as text</button>
        </div>
        {download.error && <Alert color="danger" className="mb-0" toggle={download.dismiss}>{download.error}</Alert>}
        {!pick.emailable && <p className="ep-banner ep-banner-warn mb-0">Emails can't be sent from this server: the calendar team's reply address is not set.
          Download the list or copy it, and send it yourself.</p>}
        {isEmail && <AddressPicker id={`ep-send-${entry.organizerId}`} value={pick.address} team={data.team} onChange={address => onPick({ address })} />}
        {!isEmail && <p className="ep-banner ep-banner-info mb-0">Download the list or copy it as text and send it on WhatsApp, by your own email or read it on
          the phone. When you send this, the new proposals become Proposed and the history says it was sent outside the platform.</p>}
      </>}
    </div>
    {data && <ListTable list={data} name={fullName} drafts={pick.drafts} settings={settings} timeZone={timeZone} onOpenProposal={onOpenProposal}
      onTick={(id, on) => onPick({ drafts: { ...pick.drafts, [id]: on } })} />}
  </section>
}

// "What the organizer gets": the list's sections, with the drafts to tick under New.
function ListTable({ list, name, drafts, settings, timeZone, onOpenProposal, onTick }) {
  const size = carriedOf(list, new Set(Object.keys(drafts).filter(id => drafts[id]))).length
  return <section className="ep-card ep-list-review" aria-label={`What ${name} gets`}>
    <header className="ep-panel-head"><b>{`What ${name} gets`}</b><span className="ep-meta">{`${plural(size, "tournament")} in the list`}</span>
      <span className="ep-spacer" /><span className="ep-meta">The organizer confirms the dates, club, category and play mode; competitions are informative.</span></header>
    {LIST_SECTIONS.map(([key, label, help]) => list[key].length > 0 && <div role="table" aria-label={label} key={key}>
      <div role="row" className="ep-review-group"><span role="cell"><span className="ep-mark ep-mark-soon">{`${label} (${list[key].length})`}</span>
        <span className="ep-meta">{help}</span></span></div>
      {list[key].map(item => {
        const content = item.content || item.sent
        return <div role="row" key={item.id} className={"ep-review-row" + (key === "withdrawn" ? " is-gone" : "")}>
          <span role="cell">{key === "new" && item.state === "draft" && <input type="checkbox" aria-label={`Send ${item.number}`} checked={Boolean(drafts[item.id])}
            onChange={event => onTick(item.id, event.target.checked)} />}</span>
          <span role="cell" className="ep-stack"><button type="button" className="ep-link" onClick={() => onOpenProposal(item)}>{item.number}</button>
            <span className="ep-meta">{sportLabel(content.sportType)}</span></span>
          <span role="cell" className="ep-stack"><b>{instantDaysLabel(content, timeZone)}</b><span className="ep-meta">{queueClubLabel(content.club)}</span>
            {item.changes?.length > 0 && <span className="ep-mark ep-mark-soon">{changesLine(item.changes, item.sent, timeZone)}</span>}
            {key === "new" && item.state !== "draft" && <span className="ep-mark ep-mark-soon">Not sent yet to this organizer</span>}</span>
          <span role="cell" className="ep-stack"><span><span className={`ep-dot ep-cat-${content.category}`} aria-hidden="true" />{categoryLabel(settings, content.category)}</span>
            <span className="ep-meta">{playModeLabel(content.playMode) || "No play mode"}</span></span>
          <span role="cell" className="ep-tiles">{competitionsLabel(content.competitions) || "—"}</span>
        </div>
      })}
    </div>)}
  </section>
}
